import React, { useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { MarkdownOrTextComponent } from "../../common/RenderMarkdownOrText";

import {
  AI_ROLE,
  USER_ROLE,
  LEARNING_PLAN,
  TOPIK_GENERATION,
  VOCAB_EXPANSION,
} from "../../../../constants/ai-tutor";
import { MessageTemplates } from "../../common/MessageTemplates";
import PlannerFirstChat from "./PlannerFirstChat";
import TopikGenerationFirstChat from "./TopikGenerationFirstChat";
import RenderAiPlanner from "../../aiLearningPlanner/components/RenderAiPlanner";
const Avatar = require("../../../../icons/ai-bot.png");
const AvatarUser = require("../../../../icons/user.png");

function ChatMessages({
  messages,
  isTyping,
  isStreaming,
  containerRef,
  languageText = null,
  customMessageRenderer = null,
  saveItems = null,
}) {
  const virtuosoRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const prevCountRef = useRef(0);
  const footerRef = useRef(null);
  const SCROLL_OFFSET = 500;

  const renderStructuredObject = (
    content,
    role,
    type,
    features_id,
    is_saved
  ) => {
    if (role === USER_ROLE) {
      if (type === LEARNING_PLAN) {
        return <PlannerFirstChat formData={content} />;
      }
      if (type === TOPIK_GENERATION) {
        return (
          <TopikGenerationFirstChat
            formData={content}
            languageText={languageText}
          />
        );
      }
      return <div className="message-text">{JSON.stringify(content)}</div>;
    }

    // AI object content
    if (type === LEARNING_PLAN && role === AI_ROLE) {
      return MessageTemplates.plan({
        content,
        languageText,
        saveItems,
        features_id,
        is_saved,
      });
    }
    if (type === TOPIK_GENERATION && role === AI_ROLE) {
      return MessageTemplates.topik({
        content,
        languageText,
        saveItems,
        features_id,
        is_saved,
      });
    }
    if (type === VOCAB_EXPANSION && role === AI_ROLE) {
      return MessageTemplates.vocabulary({
        content,
        languageText,
        saveItems,
        features_id,
        is_saved,
      });
    }

    return <div className="message-text">{JSON.stringify(content)}</div>;
  };

  const renderMessageContent = (message) => {
    const {
      content,
      isStreaming: msgStreaming,
      role,
      type,
      features_id,
      is_saved,
    } = message;
    const isContentObject = typeof content === "object" && content !== null;
    const streamingNow = !!msgStreaming;

    if (isContentObject) {
      return renderStructuredObject(
        content,
        role,
        type,
        features_id,
        !!is_saved
      );
    }

    // String
    const stringContent = String(content);
    if (customMessageRenderer) {
      return customMessageRenderer(message);
    }
    return MarkdownOrTextComponent(message, stringContent, streamingNow);
  };

  const data = useMemo(() => {
    const base = messages || [];
    if (isTyping && !base.some((m) => m.isStreaming)) {
      return [
        ...base,
        {
          id: "__typing__",
          role: AI_ROLE,
          isStreaming: false,
          isError: false,
          content: "",
          _isTypingRow: true,
        },
      ];
    }
    return base;
  }, [messages, isTyping]);

  // ==== Auto-scroll logic ====
  const initialIndex = Math.max(0, (data?.length ?? 1) - 1);

  useEffect(() => {
    if (isStreaming && virtuosoRef.current) {
      virtuosoRef.current.scrollTo({
        top: Number.MAX_SAFE_INTEGER,
        behavior: "smooth",
      });
      setTimeout(() => {
        virtuosoRef.current?.scrollTo({
          top: Number.MAX_SAFE_INTEGER + SCROLL_OFFSET,
          behavior: "smooth",
        });
      }, 250);
    }
  }, [isStreaming, data]);

  return (
    <div className="chat-messages h-full w-full" ref={containerRef}>
      <Virtuoso
        ref={virtuosoRef}
        data={data}
        initialTopMostItemIndex={initialIndex}
        className="h-full w-full hide-scrollbar"
        atBottomStateChange={(atBottom) => {
          isAtBottomRef.current = atBottom;
        }}
        // followOutput={(atBottom) =>
        //   isStreaming || atBottom ? "smooth" : false
        // }
        itemContent={(index, message) => {
          const isAI = message.role === AI_ROLE;
          const isUser = message.role === USER_ROLE;
          if (isAI && message.type === LEARNING_PLAN) {
            return (
              <RenderAiPlanner message={message} languageText={languageText} saveItems={saveItems}/>
            )
          } else {
            return (
              <div
                key={message.id}
                className={`message ${isAI ? "ai" : "user"}`}
              >
                <div className="message-container flex flex-column gap-2">
                  {message.isSpecialLoading &&
                    message.role === AI_ROLE &&
                    message.type === LEARNING_PLAN && (
                      <StepLoader
                        message={message}
                        languageText={languageText}
                      />
                    )}

                  <div className="flex align-items-start gap-2 message-wrapper">
                    {isAI && (
                      <div className="message-avatar">
                        <img src={Avatar} alt="AI Avatar" />
                      </div>
                    )}
                    {isUser && (
                      <div className="message-avatar user">
                        <img src={AvatarUser} alt="User Avatar" />
                      </div>
                    )}
                    <div className="message-content">
                      <div
                        className={`message-bubble ${isAI ? "ai" : "user"} ${
                          message.isError ? "error" : ""
                        }`}
                      >
                        {message._isTypingRow ? (
                          <div className="typing-indicator h-[20px]">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        ) : message.role === AI_ROLE &&
                          message.isStreaming &&
                          !message.content ? (
                          <div className="typing-indicator h-[20px]">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        ) : (
                          renderMessageContent(message)
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        }}
        components={{
          Footer: () => <div ref={footerRef} style={{ height: 8 }} />,
        }}
        increaseViewportBy={{ top: 300, bottom: 600 }}
      />
    </div>
  );
}

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.messages === nextProps.messages &&
    prevProps.isTyping === nextProps.isTyping &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.containerRef === nextProps.containerRef
  );
};

export default React.memo(ChatMessages, areEqual);
